import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import type { AcademicLevel, CapCode } from '@dosfilos/domain';
import { db } from '../lib/firebase';
import { useTenantStore } from '../stores/tenant.store';

// ─── Program CAP target ───────────────────────────────────────────────────────
// The TMAI certification a program pursues (1:1), set on the program. CAP pages
// (journey, PLOs, matrix, compliance, proposal) use this to DEFAULT their CAP +
// level selectors, so opening a step doesn't fall back to BSC and show an empty
// (wrong-CAP) state. Returns null until loaded / when the program has no target;
// the returned object is stable (set once) so callers can seed state from it in
// an effect without re-applying over a user's manual switch.

export interface ProgramCapTarget {
  capCode: CapCode;
  level: AcademicLevel;
  priorCapRecognized: boolean;
}

export function useProgramCapTarget(programId: string | undefined): ProgramCapTarget | null {
  const currentTenantId = useTenantStore((s) => s.currentTenantId);
  const [target, setTarget] = useState<ProgramCapTarget | null>(null);

  useEffect(() => {
    if (!currentTenantId || !programId) return;
    let alive = true;
    void getDoc(doc(db, `tenants/${currentTenantId}/programs/${programId}`)).then((snap) => {
      if (!alive || !snap.exists()) return;
      const ct = snap.data()['capTarget'] as
        | { capCode?: CapCode; level?: AcademicLevel; priorCapRecognized?: boolean }
        | undefined;
      if (ct?.capCode && ct?.level)
        setTarget({
          capCode: ct.capCode,
          level: ct.level,
          priorCapRecognized: ct.priorCapRecognized ?? false,
        });
    });
    return () => {
      alive = false;
    };
  }, [currentTenantId, programId]);

  return target;
}
